import { environmentStorageKey } from "@/lib/appEnvironment"
import { createStore, del, get, set, setMany, update, entries } from "idb-keyval"
import { captureSession, withInactiveDraft, withSubmissionLock } from "@/services/submissionConcurrency"
import { activeDrafts, draftKeyFor } from "@/services/draftService"
import { useAuthStore } from "@/store/authStore"
import { getActiveWorkspaceId } from "@/services/workspaceService"
import { formDraftDb } from "@/lib/formDraftDb"
import type { ImageConfig, VideoConfig } from "@/types/form-builder"
import { uploadBlob } from "@/services/blobService"

const mediaStore = createStore(environmentStorageKey("dkro-pending-media-db"), "pending-media")
const LOCAL_MEDIA_PREFIX = "offline-media://"

export type DeferredMediaValue = {
  url: string
  localMediaId: string
  name: string
  type: string
  size?: number
  lastModified?: number
  storedFileId?: string
  caption?: string
  timestamp?: string
  location?: { lat: number; lng: number } | null
  device?: string
}

export type MediaOwner = { userId: string; workspaceId: string; submissionId?: string; fieldPath?: string; editingSessionId?: string }
export type MediaProcessing = { image?: ImageConfig; video?: VideoConfig; file?: true; replacesUrl?: string }
type MediaRecord = { owner: MediaOwner; value: DeferredMediaValue; processing?: MediaProcessing; prepared: boolean; remoteUrl?: string; blobKey?: string; attached?: boolean; lastError?: string }
const metadataKey = (id: string) => `metadata:${id}`
const uploads = new Map<string, Promise<string>>()

export function currentMediaOwner(): MediaOwner {
  const userId = useAuthStore.getState().user?.id
  const workspaceId = getActiveWorkspaceId()
  if (!userId || !workspaceId) throw new Error("Entre e selecione um workspace antes de anexar mídia.")
  return { userId, workspaceId }
}
function assertOwner(owner: MediaOwner) {
  if (useAuthStore.getState().user?.id !== owner.userId) throw new Error("A mídia pertence a uma sessão diferente.")
}

export async function deferMediaUpload(file: Blob, metadata: Omit<DeferredMediaValue, "url" | "localMediaId" | "name" | "type"> = {}, owner = currentMediaOwner(), processing?: MediaProcessing, restoredCapture = false, captureId?: string): Promise<DeferredMediaValue> {
  return withSubmissionLock(`media-stage:${owner.userId}:${owner.workspaceId}:${owner.submissionId ?? ""}`, async () => {
  if (!restoredCapture) assertOwner(owner)
  if (owner.submissionId && owner.editingSessionId) {
    const { assertEditingSession } = await import("@/services/draftService")
    await assertEditingSession(draftKeyFor({ ...owner, submissionId: owner.submissionId }), owner.editingSessionId)
  }
  if (!file.size) throw new Error("Não é possível anexar um arquivo de mídia vazio.")
  const id = captureId ?? crypto.randomUUID()
  const existing = await get<MediaRecord>(metadataKey(id), mediaStore)
  if (existing) {
    if (existing.owner.userId !== owner.userId || existing.owner.workspaceId !== owner.workspaceId || existing.owner.submissionId !== owner.submissionId) throw new Error("A posse da captura não corresponde.")
    return existing.value
  }
  const name = typeof (file as { name?: unknown }).name === "string" ? (file as unknown as { name: string }).name : `media-${id}`
  const type = typeof file.type === "string" && file.type ? file.type : "application/octet-stream"
  // Guarda um `Blob` "limpo" (sem a propriedade `name` simulada via `defineProperty` em
  // imageMedia.ts) — a câmera devolve um `Blob` nativo com esse expando pra fingir ser
  // `File`, e persistir esse objeto "estranho" direto no IndexedDB é o que corrompia o
  // round-trip no WebView Android (`.type` voltava truthy mas não-string na leitura,
  // causa raiz do "TypeError: e.split is not a function" no envio). `name`/`type` já
  // ficam preservados à parte nos metadados abaixo, então nada se perde.
  const value = { ...metadata, url: `${LOCAL_MEDIA_PREFIX}${id}`, localMediaId: id, name, type }
  await setMany([
    [id, new Blob([file], { type })],
    [metadataKey(id), { owner, value, processing, prepared: !processing, attached: !owner.submissionId } satisfies MediaRecord],
  ], mediaStore)
  if (typeof navigator !== "undefined") void navigator.storage?.persist?.().catch(() => false)
  return value
  })
}

export function isDeferredMedia(value: unknown): value is DeferredMediaValue {
  if (!value || typeof value !== "object") return false
  const item = value as Record<string, unknown>
  return typeof item.localMediaId === "string" && typeof item.url === "string"
}

export async function getLocalMediaBlob(src: string): Promise<Blob | undefined> {
  if (!src.startsWith(LOCAL_MEDIA_PREFIX)) return undefined
  const id = src.slice(LOCAL_MEDIA_PREFIX.length)
  const record = await get<MediaRecord>(metadataKey(id), mediaStore)
  if (!record) throw new Error("A posse da mídia não foi verificada.")
  assertOwner(record.owner)
  const blob = await get<Blob>(record.blobKey ?? id, mediaStore)
  if (!blob?.size) throw new Error("A mídia local está ausente. O rascunho foi mantido.")
  assertOwner(record.owner)
  return new Blob([blob], { type: record.value.type || "application/octet-stream" })
}

export async function getAcknowledgedLocalMediaBlob(url: string): Promise<Blob | undefined> {
  const userId = useAuthStore.getState().user?.id
  if (!userId) return undefined
  const check = captureSession(userId)
  for (const [key, item] of await entries<string, MediaRecord | Blob>(mediaStore)) {
    if (!key.startsWith("metadata:") || !("owner" in item) || item.owner.userId !== userId || item.remoteUrl !== url) continue
    check()
    const blob = await getLocalMediaBlob(item.value.url)
    check()
    if (blob) return blob
  }
  return undefined
}

export async function normalizeOwnedMedia<T>(value: T, owner: MediaOwner): Promise<T> {
  assertOwner(owner)
  if (isDeferredMedia(value)) {
    const record = await get<MediaRecord>(metadataKey(value.localMediaId), mediaStore)
    if (record && (record.owner.userId !== owner.userId || record.owner.workspaceId !== owner.workspaceId)) throw new Error("O dono da mídia não corresponde.")
    const normalized = { ...value, url: `${LOCAL_MEDIA_PREFIX}${value.localMediaId}` }
    if (!record) await set(metadataKey(value.localMediaId), { owner, value: normalized, prepared: true, attached: false } satisfies MediaRecord, mediaStore)
    return normalized as T
  }
  if (value && typeof value === "object" && "url" in value && typeof value.url === "string" && /^data:(image|video)\//.test(value.url)) {
    const bytes = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify([owner, value.url]))))
    const id = `legacy-inline-${Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("")}`
    const blob = await (await fetch(value.url)).blob()
    const staged = await deferMediaUpload(blob, value as Partial<DeferredMediaValue>,
      { ...owner, fieldPath: owner.fieldPath?.replace(/\.\d+$/, "") }, { replacesUrl: value.url }, false, id)
    return { ...value, ...staged } as T
  }
  if (Array.isArray(value)) return await Promise.all(value.map((item, index) => normalizeOwnedMedia(item, { ...owner, fieldPath: `${owner.fieldPath ?? ""}.${index}` }))) as T
  if (value && typeof value === "object") return Object.fromEntries(await Promise.all(Object.entries(value).map(async ([key, item]) => [key, await normalizeOwnedMedia(item, { ...owner, fieldPath: owner.fieldPath ? `${owner.fieldPath}.${key}` : key })]))) as T
  return value
}

export async function prepareAndUploadMedia(value: DeferredMediaValue, owner: MediaOwner): Promise<string> {
  const check = captureSession(owner.userId)
  const id = value.localMediaId
  const initial = await get<MediaRecord>(metadataKey(id), mediaStore)
  if (!initial || initial.owner.userId !== owner.userId || initial.owner.workspaceId !== owner.workspaceId) throw new Error("O dono da mídia não corresponde.")
  check()
  const running = uploads.get(id)
  if (running) { const url = await running; check(); return url }
  const operation = (async () => {
    check()
    let record = await get<MediaRecord>(metadataKey(id), mediaStore)
    if (!record || record.owner.userId !== owner.userId || record.owner.workspaceId !== owner.workspaceId) throw new Error("O dono da mídia não corresponde.")
    if (record.remoteUrl) return record.remoteUrl
    let blob = await getLocalMediaBlob(`${LOCAL_MEDIA_PREFIX}${id}`)
    if (!blob) throw new Error("Arquivo de mídia ausente.")
    let file = blob as File
    Object.defineProperty(file, "name", { value: record.value.name, configurable: true })
    if (!record.prepared) {
      if (record.processing?.image) {
        const { compressImage } = await import("@/lib/imageCompression")
        file = await compressImage(file, record.processing.image.maxSizeKB ?? 500, record.processing.image.compressionQuality ?? 0.75)
      }
      if (record.processing?.video) {
        const { compressRecordedVideo } = await import("@/lib/nativeVideoCompression")
        const { UPLOAD_BUDGET_BYTES } = await import("@/lib/videoBitrateBudget")
        await validateVideoDuration(file, record.processing.video.maxDurationSeconds)
        file = await compressRecordedVideo(file, record.processing.video)
        if (file.size > UPLOAD_BUDGET_BYTES) throw new Error("O vídeo continua muito grande. Substitua por uma gravação mais curta.")
      }
      const name = typeof file.name === "string" ? file.name : record.value.name
      const type = typeof file.type === "string" && file.type ? file.type : record.value.type
      blob = new Blob([file], { type })
      // Keep original bytes until a complete optimized replacement is durable.
      const blobKey = `${id}:prepared`
      await set(blobKey, blob, mediaStore)
      record = { ...record, blobKey, prepared: true, value: { ...record.value, name, type } }
      const prepared = record
      await update<MediaRecord>(metadataKey(id), current => ({ ...current!, blobKey: prepared.blobKey, prepared: true, value: prepared.value }), mediaStore)
    }
    check()
    file = new Blob([blob], { type: record.value.type }) as File
    Object.defineProperty(file, "name", { value: record.value.name })
    const uploaded = await uploadBlob(file, owner.workspaceId)
    if (typeof uploaded?.url !== "string" || !uploaded.url.trim()) throw new Error("O envio não retornou uma URL de mídia.")
    // Acknowledgement belongs to the captured owner even if logout occurred during upload.
    await update<MediaRecord>(metadataKey(id), current => ({
      ...current!, remoteUrl: uploaded.url, lastError: undefined,
      ...(current?.processing?.file ? { value: {
        ...current.value, storedFileId: uploaded.storedFileId, size: uploaded.sizeBytes ?? current.value.size,
      } } : {}),
    }), mediaStore)
    check()
    return uploaded.url
  })()
  uploads.set(id, operation)
  notifyMediaChanged()
  try { return await operation }
  catch (error) {
    await update<MediaRecord | undefined>(metadataKey(id), current => current ? { ...current, lastError: error instanceof Error ? error.message : String(error) } : current, mediaStore).catch(() => undefined)
    throw error
  } finally { uploads.delete(id); notifyMediaChanged() }
}

function notifyMediaChanged() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("dkro-media-changed"))
}

export async function getMediaUploadStatus(id: string) {
  const record = await get<MediaRecord>(metadataKey(id), mediaStore)
  if (!record) throw new Error("A posse da mídia não foi resolvida. Mantenha o rascunho para recuperação.")
  assertOwner(record.owner)
  return { uploaded: !!record.remoteUrl, uploading: uploads.has(id), error: record.lastError }
}

export async function retryMediaUpload(id: string) {
  const record = await get<MediaRecord>(metadataKey(id), mediaStore)
  if (!record) throw new Error("A mídia está indisponível para nova tentativa. O rascunho foi mantido.")
  return prepareAndUploadMedia(record.value, record.owner)
}

async function materialize(value: unknown, owner: MediaOwner, resolvedIds: string[]): Promise<unknown> {
  assertOwner(owner)
  if (typeof value === "string" && value.startsWith(LOCAL_MEDIA_PREFIX)) {
    const record = await get<MediaRecord>(metadataKey(value.slice(LOCAL_MEDIA_PREFIX.length)), mediaStore)
    if (!record) throw new Error("A posse da mídia local não foi resolvida. O snapshot foi mantido.")
    // Preserve string URLs, including a photo object's url property; never nest a media object there.
    const url = await prepareAndUploadMedia(record.value, owner)
    resolvedIds.push(record.value.localMediaId)
    return url
  }
  if (isDeferredMedia(value)) {
    const url = await prepareAndUploadMedia(value, owner)
    resolvedIds.push(value.localMediaId)
    const record = await get<MediaRecord>(metadataKey(value.localMediaId), mediaStore)
    assertOwner(owner)
    const persisted = { ...value } as Partial<DeferredMediaValue>
    delete persisted.localMediaId
    // PDFs are scalar file answers: their display/download metadata must survive materialization.
    if (record?.processing?.file) {
      return { ...persisted, url, name: record.value.name, type: record.value.type,
        size: record.value.size, storedFileId: record.value.storedFileId }
    }
    delete persisted.name
    delete persisted.type
    return { ...persisted, url }
  }
  if (Array.isArray(value)) return Promise.all(value.map(item => materialize(item, owner, resolvedIds)))
  if (value && typeof value === "object") return Object.fromEntries(await Promise.all(Object.entries(value).map(async ([key, item]) => [key, await materialize(item, owner, resolvedIds)])))
  return value
}

export function collectLocalMediaIds(value: unknown, ids = new Set<string>()): Set<string> {
  if (isDeferredMedia(value)) ids.add(value.localMediaId)
  else if (typeof value === "string" && value.startsWith(LOCAL_MEDIA_PREFIX)) ids.add(value.slice(LOCAL_MEDIA_PREFIX.length))
  else if (value && typeof value === "object") Object.values(value).forEach(item => collectLocalMediaIds(item, ids))
  return ids
}

export async function releaseUnreferencedMedia(ids: Iterable<string>, discardedSessionId?: string): Promise<void> {
  const referenced = new Set<string>()
  for (const draft of await formDraftDb.drafts.toArray()) collectLocalMediaIds(draft.data, referenced)
  for (const session of await formDraftDb.sessions.toArray()) {
    if (session.phase === "editing" || session.phase === "discarding") {
      collectLocalMediaIds(session.baseline?.data, referenced)
      collectLocalMediaIds(session.suspendedPause?.data, referenced)
    }
  }
  const storedQueue = await get<string>(environmentStorageKey("dkro-offline-sync-queue"))
  if (storedQueue) collectLocalMediaIds(JSON.parse(storedQueue), referenced)
  for (const id of ids) {
    if (referenced.has(id) || uploads.has(id)) continue
    const record = await get<MediaRecord>(metadataKey(id), mediaStore)
    if (!record || record.owner.userId !== useAuthStore.getState().user?.id) continue
    const discarded = !!discardedSessionId && record.owner.editingSessionId === discardedSessionId
    if (!record.attached && !discarded) continue
    if (record.owner.submissionId && !discarded) {
      const key = draftKeyFor(record.owner as MediaOwner & { submissionId: string })
      if (activeDrafts.has(key)) continue
      const draft = await formDraftDb.drafts.get(key)
      if (draft && draft.status !== "completed") continue
    }
    await del(id, mediaStore)
    if (record.blobKey) await del(record.blobKey, mediaStore)
    await del(metadataKey(id), mediaStore)
  }
}

export async function markMediaAttached(value: unknown) {
  for (const id of collectLocalMediaIds(value)) {
    await update<MediaRecord | undefined>(metadataKey(id), current => current ? { ...current, attached: true } : current, mediaStore)
  }
}

export async function hasUnattachedMedia(owner: MediaOwner) {
  return (await entries<string, MediaRecord | Blob>(mediaStore)).some(([key, item]) => key.startsWith("metadata:") && "owner" in item && !item.attached
    && item.owner.userId === owner.userId && item.owner.workspaceId === owner.workspaceId && item.owner.submissionId === owner.submissionId)
}

/** Recover the write-ahead capture journal after interruption between blob storage and draft attachment. */
export async function recoverStagedMedia<T extends Record<string, unknown>>(owner: MediaOwner, values: T): Promise<T> {
  assertOwner(owner)
  const data = structuredClone(values)
  const ids = collectLocalMediaIds(data)
  for (const [key, item] of await entries<string, MediaRecord | Blob>(mediaStore)) {
    if (!key.startsWith("metadata:") || !("owner" in item) || item.attached || ids.has(item.value.localMediaId)) continue
    if (item.owner.userId !== owner.userId || item.owner.workspaceId !== owner.workspaceId || item.owner.submissionId !== owner.submissionId || !item.owner.fieldPath) continue
    if (item.owner.editingSessionId) {
      const session = await formDraftDb.sessions.get(draftKeyFor(item.owner as MediaOwner & { submissionId: string }))
      if (session?.sessionId !== item.owner.editingSessionId || session.phase !== "editing") continue
    }
    const parts = item.owner.fieldPath.split(".")
    let target: Record<string, unknown> = data
    for (let i = 0; i < parts.length - 1; i++) target = (target[parts[i]] ??= /^\d+$/.test(parts[i + 1]) ? [] : {}) as Record<string, unknown>
    const field = parts[parts.length - 1]
    if (item.processing?.video || item.processing?.file) target[field] = item.value
    else {
      const previous = Array.isArray(target[field]) ? target[field] as unknown[] : []
      const replace = previous.findIndex(value => (typeof value === "string" ? value : (value as { url?: string })?.url) === item.processing?.replacesUrl)
      if (item.processing?.replacesUrl && replace >= 0) previous[replace] = item.value
      else previous.push(item.value)
      target[field] = previous
    }
    ids.add(item.value.localMediaId)
  }
  return data
}

async function validateVideoDuration(blob: Blob, maxDuration: number) {
  if (typeof document === "undefined" || !maxDuration) return
  const video = document.createElement("video")
  const url = URL.createObjectURL(blob)
  try {
    const duration = await new Promise<number>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("Não foi possível verificar a duração do vídeo. Tente novamente ou substitua o vídeo.")), 15000)
      const finish = () => {
        if (Number.isFinite(video.duration) && video.duration > 0) {
          clearTimeout(timer)
          resolve(video.duration)
        }
      }
      video.onloadedmetadata = () => {
        if (Number.isFinite(video.duration)) finish()
        else video.currentTime = 1e10
      }
      video.ondurationchange = finish
      video.onseeked = finish
      video.onerror = () => { clearTimeout(timer); reject(new Error("Não foi possível ler este vídeo. Substitua por um formato compatível.")) }
      video.preload = "metadata"
      video.src = url
    })
    if (Number.isFinite(duration) && duration > maxDuration + 1) throw new Error(`O vídeo excede ${maxDuration} segundos. Substitua por um vídeo mais curto.`)
  } finally { video.removeAttribute("src"); video.load(); URL.revokeObjectURL(url) }
}

export async function stagedMediaFor(owner: MediaOwner): Promise<DeferredMediaValue[]> {
  const records = await entries<string, MediaRecord | Blob>(mediaStore)
  return records.filter(([key, item]) => key.startsWith("metadata:") && "owner" in item
    && item.owner.userId === owner.userId && item.owner.workspaceId === owner.workspaceId
    && item.owner.submissionId === owner.submissionId && (!owner.fieldPath || item.owner.fieldPath === owner.fieldPath))
    .map(([, item]) => (item as MediaRecord).value)
}

/** Envia mídias capturadas offline antes de persistir as respostas. Só remove
 * o blob local depois que todos os uploads terminaram, mantendo retry seguro. */
export async function materializeDeferredMedia<T>(value: T, workspaceId: string): Promise<{ value: T; cleanup: () => Promise<void> }> {
  const resolvedIds: string[] = []
  const owner = { ...currentMediaOwner(), workspaceId }
  const result = await materialize(value, owner, resolvedIds) as T
  return {
    value: result,
    cleanup: () => releaseUnreferencedMedia(resolvedIds),
  }
}

export async function discardDeferredMedia(value: unknown): Promise<void> {
  await releaseUnreferencedMedia(collectLocalMediaIds(value))
}

export async function sweepCompletedMedia(userId: string, workspaceId: string): Promise<void> {
  const check = captureSession(userId)
  const rows = await formDraftDb.drafts.where("[userId+workspaceId]").equals([userId, workspaceId]).toArray()
  for (const row of rows) {
    check()
    if (row.status !== "completed" || !row.revision || row.revision !== row.syncedRevision || activeDrafts.has(row.key)) continue
    if (!row.submissionId?.startsWith("event-action:") && !row.previewDismissed) continue
    await withInactiveDraft(`preview:${row.key}`, () => withInactiveDraft(row.key, () => withSubmissionLock(row.key, async () => {
      check()
      const current = await formDraftDb.drafts.get(row.key)
      if (!current || current.status !== "completed" || current.revision !== current.syncedRevision || activeDrafts.has(row.key)) return
      if (!current.submissionId?.startsWith("event-action:") && !current.previewDismissed) return
      const staged = await stagedMediaFor({ userId, workspaceId, submissionId: row.submissionId })
      check()
      await releaseUnreferencedMedia(staged.map(value => value.localMediaId))
    })))
  }
}
